import React, { useRef, useEffect, useState } from "react";
import "../../styles/ProgressTracker.css";

export interface ProgressStep {
  id: number;
  label: string;
}

export type ProgressTrackerOrientation = "horizontal" | "vertical";

interface ProgressTrackerProps {
  steps: ProgressStep[];
  currentStep: number;
  orientation?: ProgressTrackerOrientation;
  onStepChange?: (step: number) => void;
  className?: string;
  rejected?: boolean;
  withdrawn?: boolean;
  approved?: boolean;
}

const ProgressTracker: React.FC<ProgressTrackerProps> = ({
  steps,
  currentStep,
  orientation = "horizontal",
  onStepChange,
  className = "",
  rejected = false,
  withdrawn = false,
  approved = false,
}) => {
  const trackerRef = useRef<HTMLDivElement>(null);
  const barContainerRef = useRef<HTMLDivElement>(null);
  const [barStyle, setBarStyle] = useState<React.CSSProperties>({});
  const [filledStyle, setFilledStyle] = useState<React.CSSProperties>({});

  // Adjust progress bar position and fill
  useEffect(() => {
    if (!trackerRef.current || !barContainerRef.current) return;
    const stepItems = Array.from(
      trackerRef.current.querySelectorAll<HTMLElement>(".step-item")
    );
    if (stepItems.length < 2) return;
    const firstCircle = stepItems[0].querySelector<HTMLElement>(".step-circle");
    const lastCircle = stepItems[stepItems.length - 1].querySelector<HTMLElement>(".step-circle");
    if (!firstCircle || !lastCircle) return;
    if (orientation === "horizontal") {
      const barHeight = 6;
      // Use getBoundingClientRect for more reliable positioning in flex layouts
      const trackerRect = trackerRef.current.getBoundingClientRect();
      const firstRect = firstCircle.getBoundingClientRect();
      const lastRect = lastCircle.getBoundingClientRect();
      const offsetStart = firstRect.left + firstRect.width / 2 - trackerRect.left;
      const offsetEnd = lastRect.left + lastRect.width / 2 - trackerRect.left;
      setBarStyle({
        left: offsetStart,
        width: offsetEnd - offsetStart,
        top: firstRect.top + firstRect.height / 2 - trackerRect.top - barHeight / 2,
        height: barHeight,
      });
      let progressPercentage = steps.length > 1 ? ((currentStep - 1) / (steps.length - 1)) * 100 : 0;
      // Clamp to 99.9% so it never visually overflows the bar
      if (currentStep >= steps.length) {
        progressPercentage = 99.9;
      }
      setFilledStyle({
        width: `${progressPercentage}%`,
        height: "100%",
      });
    } else {
      const barWidth = 6;
      const offsetStart = firstCircle.offsetTop + firstCircle.offsetHeight / 2;
      const offsetEnd = lastCircle.offsetTop + lastCircle.offsetHeight / 2;
      setBarStyle({
        top: offsetStart,
        height: offsetEnd - offsetStart,
        left: firstCircle.offsetLeft + firstCircle.offsetWidth / 2 - barWidth / 2,
        width: barWidth,
      });
      let progressPercentage = steps.length > 1 ? ((currentStep - 1) / (steps.length - 1)) * 100 : 0;
      if (currentStep >= steps.length) {
        progressPercentage = 99.9;
      }
      setFilledStyle({
        height: `${progressPercentage}%`,
        width: "100%",
      });
    }
  }, [steps, currentStep, orientation]);

  return (
    <div
      ref={trackerRef}
      className={`progress-tracker-${orientation} ${className}`.trim()}
      style={{
        position: "relative",
        width: "100%",
        maxWidth: "100%",
        display: "flex",
        flexDirection: orientation === "vertical" ? "column" : "row",
        alignItems: "center",
        justifyContent: "center",
        margin: "0 auto"
      }}
    >
      {/* Steps */}
      {steps.map((step, idx) => {
        const isCompleted = step.id < currentStep;
        const isActive = step.id === currentStep;
        const isLast = step.id === steps.length;
        const isRejectedStep = rejected && isLast;
        const isWithdrawnStep = withdrawn && isLast;
        const isApprovedStep = approved && isLast;
        // On mobile, break all step labels into separate lines for each word
        let label = step.label;
        let isMobile = false;
        if (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 640px)').matches) {
          isMobile = true;
        }
        let labelLines: string[] = [];
        if (isMobile && label) {
          // Split all labels by spaces
          if (isRejectedStep) {
            labelLines = "Application Rejected".split(' ');
          } else if (isWithdrawnStep) {
            labelLines = "Application Withdrawn".split(' ');
          } else if (isApprovedStep) {
            labelLines = "Application Approved".split(' ');
          } else {
            labelLines = label.split(' ');
          }
        } else {
          labelLines = [isRejectedStep ? "Application Rejected" : isWithdrawnStep ? "Application Withdrawn" : isApprovedStep ? "Application Approved" : label];
        }
        return (
          <div
            key={step.id}
            className={`step-item${isCompleted ? " completed" : ""}${isActive ? " active" : ""}${isRejectedStep ? " rejected" : ""}${isWithdrawnStep ? " withdrawn" : ""}${isApprovedStep ? " approved" : ""}`}
            data-step-id={step.id}
            onClick={() => onStepChange && onStepChange(step.id)}
            style={{ cursor: onStepChange ? "pointer" : undefined, zIndex: 2 }}
          >
            <div className={`step-circle${isActive ? " step-animation" : ""}${isRejectedStep || isWithdrawnStep ? " step-rejected" : ""}${isApprovedStep ? " step-approved" : ""}`}>
              {isRejectedStep || isWithdrawnStep
                ? <span style={{ color: '#fff'}}>&#10005;</span>
                : (isCompleted || isApprovedStep) ? <span>&#10003;</span> : step.id}
            </div>
            <div className={`step-label${isRejectedStep || isWithdrawnStep ? " step-label-rejected" : ""}${isApprovedStep ? " step-label-approved" : ""}`}>{
              labelLines.map((line, i) => <span key={i} style={isMobile ? { display: 'block' } : {}}>{line}</span>)
            }</div>
          </div>
        );
      })}
      {/* Progress Bar Track */}
      <div
        className="progress-bar-container"
        ref={barContainerRef}
        style={{
          ...barStyle,
          position: "absolute",
          left: barStyle.left,
          width: orientation === 'horizontal' && rejected && barStyle.width ? `calc(${barStyle.width}px - 24px)` : barStyle.width,
          top: barStyle.top,
          height: barStyle.height,
          right: undefined,
          zIndex: 1
        }}
      >
        <div
          className="progress-bar-filled"
          style={{
            ...filledStyle,
            backgroundColor: '#4a5568'
          }}
        />
      </div>
    </div>
  );
};

export default ProgressTracker;
